import "./styles.css";
import { setupAppUpdate } from "./app-update";
import {
  createJourney,
  createJourneyFiles,
  createZip,
  DEFAULT_OPTIONS,
  PapaGpsError,
  parseRouteFile,
  sanitizeBaseName,
  type GeneratedFile,
  type RouteOptions,
  type ParsedKml
} from "./core";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
type View = "upload" | "preview" | "success";
type DownloadFormat = "zip" | "gpx";
interface PreparedDownload {
  files: GeneratedFile[];
  format: DownloadFormat;
  archive?: ReturnType<typeof createZip>;
  urls?: string[];
}
interface JourneySession {
  parsed?: ParsedKml;
  name?: string;
  maxPoints: string;
  repeatBoundary: boolean;
  format: DownloadFormat;
  download?: PreparedDownload;
}
interface NavigationEntry { view: View; journey: JourneySession }

const getElement = <T extends HTMLElement>(id: string): T => {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Élément d’interface introuvable : ${id}`);
  return element as T;
};

const fileInput = getElement<HTMLInputElement>("file-input");
const chooseFileButton = getElement<HTMLButtonElement>("choose-file-button");
const dropZone = getElement<HTMLElement>("drop-zone");
const errorBox = getElement<HTMLElement>("error-box");
const generateButton = getElement<HTMLButtonElement>("generate-button");
const installButton = getElement<HTMLButtonElement>("install-button");
const maxPointsInput = getElement<HTMLInputElement>("max-points");
const repeatBoundaryInput = getElement<HTMLInputElement>("repeat-boundary");
const optionsError = getElement<HTMLElement>("options-error");
const settingsDetails = getElement<HTMLDetailsElement>("settings-details");
const journeyNameInput = getElement<HTMLInputElement>("journey-name");
const formatInputs = document.querySelectorAll<HTMLInputElement>("[data-download-format]");

let journey: JourneySession = {
  maxPoints: String(DEFAULT_OPTIONS.maxPoints),
  repeatBoundary: DEFAULT_OPTIONS.repeatBoundary,
  format: "zip"
};
let currentView: View = "upload";
let fileRequest = 0;
const navigationSession = `${Date.now()}-${Math.random()}`;
const navigationEntries: NavigationEntry[] = [{ view: "upload", journey }];
let navigationIndex = 0;
let navigationPending = false;
let downloadRequest = 0;
let pendingInstallPrompt: BeforeInstallPromptEvent | undefined;
const appUpdate = setupAppUpdate({ buildId: import.meta.env.APP_BUILD_ID ?? "development" });

function setText(id: string, value: string): void {
  getElement(id).textContent = value;
}

function setView(view: View, moveFocus = true): void {
  const views: View[] = ["upload", "preview", "success"];
  const step = views.indexOf(view);
  for (const [index, name] of views.entries()) {
    getElement(`${name}-section`).hidden = name !== view;
    const indicator = getElement(`step-${index + 1}`);
    const button = indicator.querySelector("button")!;
    button.disabled = name === view || (name === "preview" && !journey.parsed) || (name === "success" && !journey.download);
    indicator.classList.toggle("is-current", index === step);
    indicator.classList.toggle("is-complete", index < step);
    if (index === step) indicator.setAttribute("aria-current", "step");
    else indicator.removeAttribute("aria-current");
    indicator.querySelector(".step-number")!.textContent = index < step ? "✓" : String(index + 1);
    if (index === step) button.setAttribute("aria-current", "step");
    else button.removeAttribute("aria-current");
  }
  getElement("view-navigation").hidden = view === "upload";
  setText("back-label", view === "success" ? "Retour à la vérification du trajet" : "Retour au choix du fichier");
  getElement("current-journey").hidden = !journey.parsed;
  setText("current-journey-name", journey.parsed ? effectiveName() : "");
  getElement("convertisseur").dataset.view = view;
  currentView = view;
  if (moveFocus) {
    getElement(`${view}-title`).focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
  }
}

function saveChoices(): void {
  journey.maxPoints = maxPointsInput.value;
  journey.repeatBoundary = repeatBoundaryInput.checked;
  journey.name = journeyNameInput.value;
}

function effectiveName(): string {
  return sanitizeBaseName(journey.name ?? journey.parsed?.baseName ?? "Trajet");
}

function validateName(): boolean {
  const name = effectiveName();
  const message = !journeyNameInput.value.trim()
    ? "Donnez un nom à votre trajet pour préparer les fichiers."
    : new TextEncoder().encode(name).length > 200
      ? "Ce nom est trop long pour les fichiers. Choisissez un nom plus court."
      : "";
  setText("journey-name-error", message);
  getElement("journey-name-error").hidden = !message;
  if (message) journeyNameInput.setAttribute("aria-invalid", "true");
  else journeyNameInput.removeAttribute("aria-invalid");
  setText("journey-name-example", message ? "" : `Exemple de fichier : ${name} Aller 01.gpx`);
  return !message;
}

function updateFormatControls(): void {
  formatInputs.forEach(input => { input.checked = input.value === journey.format; });
  getElement("multiple-download-note").hidden = journey.format !== "gpx";
  getElement("success-multiple-download-note").hidden = journey.format !== "gpx";
  const label = journey.format === "zip" ? "Télécharger le ZIP complet" : "Télécharger les GPX séparés";
  setText("generate-label", label);
  setText("download-again-label", label);
}

function historyState(): object {
  return { papaGps: { session: navigationSession, index: navigationIndex } };
}

function presentEntry(moveFocus = true): void {
  const entry = navigationEntries[navigationIndex]!;
  journey = entry.journey;
  maxPointsInput.value = journey.maxPoints;
  repeatBoundaryInput.checked = journey.repeatBoundary;
  journeyNameInput.value = journey.name ?? journey.parsed?.baseName ?? "";
  updateFormatControls();
  // An old success entry must not show files made with settings that have since changed.
  if (entry.view !== "upload" && !journey.parsed) entry.view = "upload";
  if (entry.view === "success" && !journey.download) entry.view = "preview";
  if (entry.view === "preview" && journey.parsed) renderPreview(journey.parsed);
  if (entry.view === "success") renderDownload();
  setView(entry.view, moveFocus);
}

function cancelFileRead(): void {
  fileRequest += 1;
  chooseFileButton.disabled = false;
  dropZone.removeAttribute("aria-busy");
  dropZone.classList.remove("drop-zone--active");
  setText("file-status", "");
}

function cancelDownloads(): void {
  downloadRequest += 1;
  setDownloadBusy(false);
  setText("download-progress", "");
}

function setDownloadBusy(busy: boolean): void {
  getElement<HTMLButtonElement>("download-again-button").disabled = busy;
  document.querySelectorAll<HTMLFieldSetElement>(".download-format").forEach(fieldset => { fieldset.disabled = busy; });
}

function navigate(view: View): void {
  if (navigationPending || view === currentView) return;
  if ((view === "preview" && !journey.parsed) || (view === "success" && !journey.download)) return;
  saveChoices();
  cancelFileRead();
  if (view !== "success") cancelDownloads();
  clearError();
  const direction = ["upload", "preview", "success"].indexOf(view) < ["upload", "preview", "success"].indexOf(currentView) ? -1 : 1;
  for (let index = navigationIndex + direction; index >= 0 && index < navigationEntries.length; index += direction) {
    const entry = navigationEntries[index]!;
    if (entry.journey === journey && entry.view === view) {
      navigationPending = true;
      window.history.go(index - navigationIndex);
      return;
    }
  }
  pushEntry(view);
}

function pushEntry(view: View): void {
  navigationEntries.splice(navigationIndex + 1);
  navigationEntries.push({ view, journey });
  navigationIndex += 1;
  window.history.pushState(historyState(), "");
  presentEntry();
}

window.addEventListener("popstate", (event: PopStateEvent) => {
  const state = event.state?.papaGps;
  if (state?.session !== navigationSession || !navigationEntries[state.index]) return;
  saveChoices();
  cancelFileRead();
  clearError();
  navigationPending = false;
  navigationIndex = state.index;
  const entry = navigationEntries[navigationIndex]!;
  if (entry.view !== "success" || entry.journey !== journey) cancelDownloads();
  presentEntry();
});

function clearError(): void {
  errorBox.textContent = "";
  errorBox.hidden = true;
}

function showError(message: string): void {
  errorBox.textContent = message;
  errorBox.hidden = false;
  setView("upload");
}

function fileCountLabel(count: number): string {
  return `${count} fichier${count > 1 ? "s" : ""}`;
}

function currentOptions(): RouteOptions {
  return { maxPoints: maxPointsInput.valueAsNumber, repeatBoundary: repeatBoundaryInput.checked };
}

function updatePartsPreview(): void {
  if (!journey.parsed) return;
  const validName = validateName();
  optionsError.hidden = true;
  optionsError.textContent = "";
  maxPointsInput.removeAttribute("aria-invalid");
  getElement("boundary-note").hidden = repeatBoundaryInput.checked;
  try {
    const options = currentOptions();
    const parts = createJourney(journey.parsed.points, options);
    setText("summary-files", String(parts.aller.length + parts.retour.length));
    setText("outbound-description", fileCountLabel(parts.aller.length));
    setText("return-description", fileCountLabel(parts.retour.length));
    setText("settings-summary", `${options.maxPoints} points maximum · raccordement ${options.repeatBoundary ? "activé" : "désactivé"}`);
    generateButton.disabled = !validName;
  } catch (error) {
    optionsError.textContent = error instanceof Error ? error.message : "Vérifiez les réglages du découpage.";
    optionsError.hidden = false;
    settingsDetails.open = true;
    maxPointsInput.setAttribute("aria-invalid", "true");
    generateButton.disabled = true;
    setText("settings-summary", "Un réglage est à corriger");
    setText("summary-files", "—");
    setText("outbound-description", "—");
    setText("return-description", "—");
  }
}

function renderPreview(parsed: ParsedKml): void {
  const firstPoint = parsed.points[0];
  const lastPoint = parsed.points.at(-1);
  if (!firstPoint || !lastPoint) throw new PapaGpsError("Le départ ou l’arrivée du trajet est introuvable.");
  setText("summary-points", String(parsed.points.length));
  setText("summary-sections", `${parsed.sectionCount} tronçon${parsed.sectionCount > 1 ? "s réunis" : ""}`);
  setText("summary-start", firstPoint.name);
  setText("summary-finish", lastPoint.name);
  setText("points-summary", `Voir les ${parsed.points.length} étapes dans l’ordre`);
  const mergedText = parsed.mergedJunctions > 0
    ? ` ${parsed.mergedJunctions} point${parsed.mergedJunctions > 1 ? "s répétés" : " répété"} à la jonction des tronçons ${parsed.mergedJunctions > 1 ? "ont été réunis" : "a été réuni"}.`
    : "";
  setText("source-description", `Les étapes suivent l’ordre de votre fichier.${mergedText}`);
  const pointList = getElement("point-list");
  const fragment = document.createDocumentFragment();
  for (const point of parsed.points) {
    const item = document.createElement("li");
    item.textContent = point.name;
    fragment.append(item);
  }
  pointList.replaceChildren(fragment);
  pointList.closest("details")!.open = false;
  settingsDetails.open = false;
  updatePartsPreview();
}

async function handleFile(file: File): Promise<void> {
  if (navigationPending) return;
  saveChoices();
  const sourceJourney = journey;
  const request = ++fileRequest;
  clearError();
  chooseFileButton.disabled = true;
  dropZone.setAttribute("aria-busy", "true");
  setText("file-status", "Lecture de votre trajet…");
  try {
    const parsed = await parseRouteFile(file);
    if (request !== fileRequest) return;
    if (sourceJourney.parsed) {
      journey = { maxPoints: sourceJourney.maxPoints, repeatBoundary: sourceJourney.repeatBoundary, format: sourceJourney.format };
      // Replace the choice screen; Back returns to the file that was just selected.
      navigationEntries[navigationIndex] = { view: "upload", journey };
    }
    journey.parsed = parsed;
    journey.name = parsed.baseName;
    pushEntry("preview");
  } catch (error) {
    if (request !== fileRequest) return;
    showError(error instanceof PapaGpsError ? error.message : "Le fichier n’a pas pu être lu. Essayez de le choisir à nouveau.");
  } finally {
    if (request === fileRequest) {
      chooseFileButton.disabled = false;
      dropZone.removeAttribute("aria-busy");
      setText("file-status", "");
    }
  }
}

function openFilePicker(): void {
  // Keep the current journey until a replacement is actually selected.
  fileInput.value = "";
  fileInput.click();
}

function triggerDownload(url: string, name: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
}

async function downloadFiles(download: PreparedDownload, request: number): Promise<void> {
  if (download.archive) {
    const url = URL.createObjectURL(download.archive.blob);
    triggerDownload(url, download.archive.name);
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }
  // Keep these URLs for individual links and permission prompts for this journey.
  download.urls ??= download.files.map(file => URL.createObjectURL(new Blob([file.content], { type: "application/gpx+xml" })));
  for (const [index, file] of download.files.entries()) {
    // A short interval avoids dropping downloads when a browser receives a burst.
    if (index > 0) await new Promise(resolve => window.setTimeout(resolve, 250));
    if (request !== downloadRequest) return;
    triggerDownload(download.urls[index]!, file.name);
    setText("download-progress", `Lancement du fichier ${index + 1} sur ${download.files.length}…`);
  }
}

function renderDownload(): void {
  const download = journey.download;
  if (!download || !journey.parsed) return;
  const multiple = download.format === "gpx";
  setText("download-name", download.archive?.name ?? effectiveName());
  setText("download-tag", multiple ? "GPX" : "ZIP");
  setText("success-description", `${fileCountLabel(download.files.length)} GPX · aller et retour inclus`);
  setText("success-intro", multiple
    ? `Vérifiez que votre navigateur a bien récupéré les ${download.files.length} fichiers ci-dessous.`
    : "Le téléchargement a été lancé. Il ne reste qu’à les mettre sur le Garmin.");
  setText("download-instructions-title", multiple ? "Retrouvez les fichiers téléchargés" : "Ouvrez le fichier téléchargé");
  getElement("zip-instructions").hidden = multiple;
  getElement("gpx-instructions").hidden = !multiple;
  getElement("individual-downloads").hidden = !multiple;
  const fragment = document.createDocumentFragment();
  if (multiple) {
    download.files.forEach((file, index) => {
      const item = document.createElement("li");
      const link = document.createElement("a");
      link.href = download.urls![index]!;
      link.download = file.name;
      link.setAttribute("aria-label", `Télécharger ${file.name}`);
      link.innerHTML = '<span></span><svg class="icon" aria-hidden="true"><use href="#icon-download" /></svg>';
      link.querySelector("span")!.textContent = file.name;
      item.append(link);
      fragment.append(item);
    });
  }
  getElement("individual-file-list").replaceChildren(fragment);
}

async function handleDownload(): Promise<void> {
  if (navigationPending) return;
  if (!journey.parsed) {
    showError("Choisissez d’abord le fichier de votre trajet.");
    return;
  }
  try {
    saveChoices();
    if (!validateName()) {
      journeyNameInput.focus();
      return;
    }
    if (!journey.download || journey.download.format !== journey.format) {
      const name = effectiveName();
      const files = createJourneyFiles(name, journey.parsed.points, currentOptions());
      const download = { files, format: journey.format, archive: journey.format === "zip" ? createZip(name, files) : undefined };
      journey.download?.urls?.forEach(url => URL.revokeObjectURL(url));
      journey.download = download;
    }
    const request = ++downloadRequest;
    setDownloadBusy(true);
    const pending = downloadFiles(journey.download, request);
    if (currentView === "success") renderDownload();
    else navigate("success");
    await pending;
    if (request !== downloadRequest) return;
    setText("download-progress", "");
    setDownloadBusy(false);
  } catch (error) {
    cancelDownloads();
    navigationEntries[navigationIndex]!.view = "preview";
    presentEntry();
    settingsDetails.open = true;
    optionsError.textContent = error instanceof Error ? error.message : "Les fichiers n’ont pas pu être créés. Réessayez.";
    optionsError.hidden = false;
  }
}

function startNewJourney(): void {
  if (navigationPending) return;
  saveChoices();
  cancelFileRead();
  cancelDownloads();
  journey = { maxPoints: journey.maxPoints, repeatBoundary: journey.repeatBoundary, format: journey.format };
  fileInput.value = "";
  clearError();
  pushEntry("upload");
}

chooseFileButton.addEventListener("click", openFilePicker);
getElement("back-button").addEventListener("click", () => navigate(currentView === "success" ? "preview" : "upload"));
getElement("resume-button").addEventListener("click", () => navigate("preview"));
document.querySelectorAll<HTMLButtonElement>(".progress-steps button").forEach(button => {
  button.addEventListener("click", () => navigate(button.dataset.view as View));
});
document.querySelector<HTMLAnchorElement>(".skip-link")!.addEventListener("click", event => {
  event.preventDefault();
  getElement("convertisseur").focus();
});
getElement("new-journey-button").addEventListener("click", startNewJourney);
getElement("back-to-preview-button").addEventListener("click", () => navigate("preview"));

function updateChoices(): void {
  journey.download?.urls?.forEach(url => URL.revokeObjectURL(url));
  journey.download = undefined;
  saveChoices();
  updateFormatControls();
  updatePartsPreview();
  setView(currentView, false);
}
getElement("reset-settings-button").addEventListener("click", () => {
  maxPointsInput.value = String(DEFAULT_OPTIONS.maxPoints);
  repeatBoundaryInput.checked = DEFAULT_OPTIONS.repeatBoundary;
  updateChoices();
});
maxPointsInput.addEventListener("input", updateChoices);
repeatBoundaryInput.addEventListener("change", updateChoices);
journeyNameInput.addEventListener("input", updateChoices);
formatInputs.forEach(input => {
  input.addEventListener("change", () => {
    if (!input.checked) return;
    cancelDownloads();
    journey.format = input.value as DownloadFormat;
    updateFormatControls();
  });
});
generateButton.addEventListener("click", handleDownload);
getElement("download-again-button").addEventListener("click", handleDownload);

fileInput.addEventListener("change", () => {
  const selectedFile = fileInput.files?.[0];
  if (selectedFile) void handleFile(selectedFile);
});
for (const eventName of ["dragenter", "dragover"]) {
  dropZone.addEventListener(eventName, (event) => {
    event.preventDefault();
    dropZone.classList.add("drop-zone--active");
  });
}
for (const eventName of ["dragleave", "drop"]) {
  dropZone.addEventListener(eventName, (event) => {
    event.preventDefault();
    dropZone.classList.remove("drop-zone--active");
  });
}
dropZone.addEventListener("drop", (event) => {
  const files = event.dataTransfer?.files;
  if (files && files.length > 1) {
    cancelFileRead();
    showError("Choisissez un seul fichier à la fois : le KML ou KMZ de votre carte entière, ou un itinéraire GPX.");
    return;
  }
  const droppedFile = files?.[0];
  if (droppedFile) void handleFile(droppedFile);
});

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault();
  pendingInstallPrompt = event as BeforeInstallPromptEvent;
  installButton.hidden = false;
});
installButton.addEventListener("click", async () => {
  if (!pendingInstallPrompt) return;
  try {
    await pendingInstallPrompt.prompt();
    await pendingInstallPrompt.userChoice;
  } catch {
    setText("offline-status", "Pour installer l’application, utilisez le menu de votre navigateur.");
  } finally {
    pendingInstallPrompt = undefined;
    installButton.hidden = true;
  }
});
window.addEventListener("appinstalled", () => {
  pendingInstallPrompt = undefined;
  installButton.hidden = true;
});

async function registerServiceWorker(): Promise<void> {
  if (import.meta.env.MODE === "offline" || window.location.protocol === "file:") {
    setText("offline-status", "Version autonome · disponible hors ligne");
    return;
  }
  if (import.meta.env.DEV || !("serviceWorker" in navigator) || !/^https?:$/.test(window.location.protocol)) return;
  try {
    const registration = await navigator.serviceWorker.register("./sw.js");
    appUpdate.watch(registration);
    await navigator.serviceWorker.ready;
    setText("offline-status", "Prête pour une utilisation hors ligne");
  } catch {
    // Conversion still works when offline caching is unavailable.
  }
}

window.history.replaceState(historyState(), "");
presentEntry(false);
void registerServiceWorker();
